import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MapPin } from 'lucide-react';

interface Location {
  id: string;
  name: string;
  subLocations?: Location[];
}

interface LocationSelectorProps {
  locations: Location[];
  onLocationChange: (locationId: string) => void;
  value: string;
  className?: string;
}

export function LocationSelector({ locations, onLocationChange, value, className }: LocationSelectorProps) {
  // Flatten locations for rendering in the select
  const flattenLocations = (locs: Location[], depth = 0): { id: string; name: string; depth: number }[] => {
    return locs.reduce<{ id: string; name: string; depth: number }[]>((acc, loc) => {
      acc.push({ id: loc.id, name: loc.name, depth });
      if (loc.subLocations && loc.subLocations.length > 0) {
        acc.push(...flattenLocations(loc.subLocations, depth + 1));
      }
      return acc;
    }, []);
  };

  const flatLocations = flattenLocations(locations);

  // Format location name with indentation based on depth
  const formatLocationName = (name: string, depth: number) => {
    if (depth === 0) return name;
    return `${'\u00A0'.repeat(depth * 2)}${name}`;
  };

  return (
    <Select value={value || 'all'} onValueChange={(val) => onLocationChange(val === 'all' ? '' : val)}>
      <SelectTrigger className={`w-[240px] ${className || ''}`}>
        <SelectValue placeholder="Select Location" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectItem value="all">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              All Locations
            </div>
          </SelectItem>

          {flatLocations.map((loc) => (
            <SelectItem key={loc.id} value={loc.id}>
              <div className="flex items-center gap-2">
                {loc.depth === 0 && <MapPin className="h-4 w-4" />}
                {formatLocationName(loc.name, loc.depth)}
              </div>
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
